import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Progress, message } from "antd";

import HighnoteSignature from "components/highnote-signature/highnote-signature";
import ProofCenterHeader from "../../components/proof-center-header/proof-center-header";

import { useGetPcCategoriesAndRequirementsQuery } from "graphql/queries/get-pc-categories-and-requirements.generated";
import { useGetProofCenterPageDataQuery } from "graphql/queries/get-proof-center-page-data.generated";

import { getPropertyAddress } from "utils/helpers";
import { ProofCenterCategoryKeys, ProofCenterStatus } from "constants/field-values";
import routeNames from "constants/route-names";

import { categoryMap as CategoryMap } from "components/proof-center-svgs/proof-center-svgs";

// Mock data types
interface Subsystem {
	id: string;
	name: string;
	description: string;
	status: "operational" | "warning" | "outage";
	icon?: string;
}

const getStatusColor = (status: Subsystem["status"]) => {
	switch (status) {
		case "operational":
			return "#52c41a";
		case "warning":
			return "#faad14";
		case "outage":
			return "#ff4d4f";
		default:
			return "#52c41a";
	}
};

const getStatusLabel = (status: Subsystem["status"]) => {
	switch (status) {
		case "operational":
			return "Operational";
		case "warning":
			return "Warning";
		case "outage":
			return "Outage";
		default:
			return "Operational";
	}
};

const getStatusImage = (status: Subsystem["status"]) => {
	switch (status) {
		case "operational":
			return "checkmark-green-circle.svg";
		case "warning":
			return "incident.svg";
		case "outage":
			return "outage.svg";
		default:
			return "Operational";
	}
};

const getProgressColor = (percentage: number) => {
	if (percentage >= 80) return "#52c41a";
	if (percentage >= 50) return "#faad14";
	return "#ff4d4f";
};

const ProofCenter: React.FC = () => {
	const navigate = useNavigate();
	const { propertyId } = useParams<{ propertyId: string }>();

	const handlePropertyNotFound = useCallback(() => {
		message.error("Sorry, that property does not exist.");
		navigate(routeNames.HOME);
	}, [navigate]);

	const { data: pageData } = useGetProofCenterPageDataQuery({
		skip: !propertyId,
		variables: { propertyId: propertyId! },
		fetchPolicy: "network-only",
		onCompleted: data => {
			if (!data.properties_by_pk || data.properties_by_pk.length === 0) {
				handlePropertyNotFound();
			}
		},
		onError: () => {
			handlePropertyNotFound();
		}
	});

	const property = useMemo(() => pageData?.properties_by_pk?.[0], [pageData]);

	useEffect(() => {
		if (!propertyId) {
			handlePropertyNotFound();
		}
	}, [propertyId, handlePropertyNotFound]);

	const handleSystemClick = (systemId: string) => {
		navigate(routeNames.PROOF_CENTER_SYSTEM_DETAILS.replace(":propertyId", propertyId!).replace(":systemId", systemId));
	};

	const { data: categoriesData } = useGetPcCategoriesAndRequirementsQuery();

	// Create a map of requirement ID to completion status
	const requirementCompletionMap = React.useMemo(() => {
		const map = new Map<string, boolean>();
		pageData?.proof_center_property_category_requirements?.forEach(req => {
			map.set(req.pc_category_requirement_id, req.is_completed);
		});
		return map;
	}, [pageData]);

	return (
		<div className="proof-center-container">
			<div className="proof-center-page-content">
				<ProofCenterHeader
					propertyName={property?.name}
					propertyAddress={getPropertyAddress(property?.address, property?.standardized_address)}
					propertyPhotoLink={property?.photo_link}
					headline={property?.proof_center_headline}
					isDraft={property?.proof_center_status === ProofCenterStatus.draft}
				/>

				{/* Info Banner */}
				{property?.proof_center_summary && (
					<div className="proof-center-info-banner">{property.proof_center_summary}</div>
				)}

				{/* Main Content */}
				<div className="proof-center-main-content">
					{(property?.proof_center_about || !!pageData?.proof_center_property_certificates.length) && (
						<div className="proof-center-left-sidebar">
							{!!pageData?.proof_center_property_certificates.length && (
								<div className="proof-center-certifications-section">
									<div className="proof-center-section-title">Certifications</div>
									<div className="proof-center-certifications-list">
										{pageData.proof_center_property_certificates.map(cert => {
											const icon = cert.proof_center_certificate.icon.startsWith("/")
												? `${process.env.PUBLIC_URL}${cert.proof_center_certificate.icon}`
												: cert.proof_center_certificate.icon;
											return (
												<div key={cert.id} className="proof-center-certification-item">
													<div className="proof-center-certification-icon">
														<img
															src={icon}
															alt=""
															onError={e => {
																(e.target as HTMLImageElement).style.display = "none";
															}}
														/>
													</div>
													<span className="proof-center-certification-name">{cert.proof_center_certificate.label}</span>
												</div>
											);
										})}
									</div>
								</div>
							)}

							{property?.proof_center_about && (
								<div className="proof-center-about-section">
									<div className="proof-center-section-title">About</div>
									<div className="proof-center-about-section-main">
										<p className="proof-center-about-text">{property.proof_center_about}</p>
										<p className="proof-center-last-updated">Last updated: [timestamp]</p>
									</div>
								</div>
							)}
						</div>
					)}

					{/* Right Content */}
					<div className="proof-center-right-content">
						{/* Systems Section */}
						<div className="proof-center-systems-section">
							<div className="proof-center-systems-header">
								<div className="proof-center-section-title">Systems</div>
								<div className="proof-center-systems-last-updated">
									UPDATED 30 MINUTES AGO
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark-in-circle-small-gray.svg`} />
								</div>
							</div>
							<div className="proof-center-systems-grid">
								{(categoriesData?.proof_center_categories || []).map(system => {
									const Icon = CategoryMap[system.key || ProofCenterCategoryKeys.FIRE_SAFETY];
									const propertyRequirements = system.proof_center_category_requirements.filter(item =>
										requirementCompletionMap.has(item.id)
									);
									if (propertyRequirements.length === 0) return null;
									const total = propertyRequirements.length;
									const completed = propertyRequirements.filter(item => requirementCompletionMap.get(item.id)).length;
									const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

									return (
										<div
											key={system.id}
											className="proof-center-system-card"
											onClick={() => handleSystemClick(system.id)}
										>
											<div className="proof-center-system-header">
												<Progress
													type="circle"
													percent={percentage}
													size={52}
													strokeWidth={16}
													strokeColor={getProgressColor(percentage)}
													format={() => <Icon stroke={getProgressColor(percentage)} />}
												/>
												<div className="proof-center-system-info">
													<div className="proof-center-system-name">{system.name}</div>
													<span className="proof-center-sub-label" style={{ color: getProgressColor(percentage) }}>
														{percentage}%
													</span>
												</div>
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/arrow-purple.svg`} />
											</div>
											<p className="proof-center-system-description">{system.description}</p>
											<div className="proof-center-check-list">
												{propertyRequirements.map(item => {
													const isCompleted = requirementCompletionMap.get(item.id) ?? false;
													return (
														<div key={item.id} className="proof-center-check-item">
															<div className={`proof-center-checkbox ${isCompleted ? "proof-center-checked" : ""}`}>
																{isCompleted && (
																	<svg viewBox="0 0 12 12" fill="none">
																		<path
																			d="M2 6L5 9L10 3"
																			stroke="currentColor"
																			strokeWidth="2"
																			strokeLinecap="round"
																			strokeLinejoin="round"
																		/>
																	</svg>
																)}
															</div>
															<span className="proof-center-check-label">{item.name}</span>
														</div>
													);
												})}
											</div>
										</div>
									);
								})}
							</div>
						</div>

						{!!pageData?.properties_subsystems.length && (
							<div className="proof-center-subsystems-section">
								<div className="proof-center-subsystems-header">
									<div className="proof-center-section-title">Subsystems</div>
									<div className="proof-center-status-legend">
										<div className="proof-center-legend-item">
											<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark-green-circle.svg`} />
											<span className="proof-center-status-label" style={{ color: getStatusColor("operational") }}>
												Operational
											</span>
										</div>
										<div className="proof-center-legend-item">
											<img src={`${process.env.PUBLIC_URL}/assets/images/icons/incident.svg`} />
											<span className="proof-center-status-label" style={{ color: getStatusColor("warning") }}>
												Warning
											</span>
										</div>
										<div className="proof-center-legend-item">
											<img src={`${process.env.PUBLIC_URL}/assets/images/icons/outage.svg`} />
											<span className="proof-center-status-label" style={{ color: getStatusColor("outage") }}>
												Outage
											</span>
										</div>
									</div>
								</div>

								<div className="proof-center-subsystems-list">
									{pageData.properties_subsystems.map(propertySubsystem => (
										<div key={propertySubsystem.id} className="proof-center-subsystem-item">
											<div className="proof-center-subsystem-icon">
												<img
													src={`${process.env.PUBLIC_URL}/assets/images/icons/${propertySubsystem.subsystem.image}`}
													alt=""
													onError={e => {
														(e.target as HTMLImageElement).style.display = "none";
													}}
												/>
											</div>
											<div className="proof-center-subsystem-info">
												<div className="proof-center-subsystem-name">{propertySubsystem.subsystem.name}</div>
												<div className="proof-center-subsystem-description">
													{propertySubsystem.subsystem.description}
												</div>
											</div>
											<div className="proof-center-subsystem-status">
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/${getStatusImage("operational")}`} />
												<span className="proof-center-status-label" style={{ color: getStatusColor("operational") }}>
													{getStatusLabel("operational")}
												</span>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="proof-center-footer">
					<HighnoteSignature />
				</div>
			</div>
		</div>
	);
};

export default ProofCenter;
